import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { ShareIcon, LinkIcon, PhotoIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';
import TripCard from './TripCard';

const spin = keyframes`to { transform: rotate(360deg); }`;

const Wrapper = styled.div`
  position: relative;
`;

const TriggerBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  padding: 8px;

  &:active {
    transform: scale(0.92);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 8px;
  min-width: 200px;
  z-index: 200;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  text-align: left;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
    color: #6b7280;
  }
`;

const Spinner = styled.div`
  width: 17px;
  height: 17px;
  border: 2px solid rgba(66, 133, 244, 0.2);
  border-top-color: #4285F4;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  flex-shrink: 0;
`;

const ShareMenu = ({ itineraryData, extractedCity, onShareLink, onDownloadText }) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const wrapperRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleShareImage = useCallback(async () => {
    setGenerating(true);
    setShowCard(true);

    // Wait for card to render and images to load
    await new Promise(r => setTimeout(r, 500));

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: 1200,
        height: 630,
      });

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'trip-card.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: itineraryData.title || 'My Trip',
          });
        } catch { /* cancelled */ }
      } else {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'trip-card.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to generate trip card:', err);
    }

    setGenerating(false);
    setShowCard(false);
    setOpen(false);
  }, [itineraryData]);

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerBtn onClick={() => setOpen(o => !o)} aria-label="Share" title="Share">
        <ShareIcon style={{ width: '20px', height: '20px', color: '#374151' }} />
      </TriggerBtn>

      {open && (
        <Dropdown>
          <MenuItem onClick={() => { onShareLink(); setOpen(false); }}>
            <LinkIcon />
            Copy link
          </MenuItem>
          <MenuItem onClick={handleShareImage} disabled={generating}>
            {generating ? <Spinner /> : <PhotoIcon />}
            {generating ? 'Generating...' : 'Share as image'}
          </MenuItem>
          <MenuItem onClick={() => { onDownloadText(); setOpen(false); }}>
            <ArrowDownTrayIcon />
            Download text
          </MenuItem>
        </Dropdown>
      )}

      {showCard && (
        <TripCard
          ref={cardRef}
          itineraryData={itineraryData}
          extractedCity={extractedCity}
        />
      )}
    </Wrapper>
  );
};

export default ShareMenu;
